import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  FileText, 
  BarChart3, 
  Users, 
  Settings, 
  Tool, 
  MessageSquare,
  Gamepad2,
  Presentation,
  HelpCircle,
  Zap,
  Webhook,
  UserCheck,
  TrendingUp,
  Clock,
  CheckCircle,
  Loader2,
  AlertCircle,
  Shield,
  Code
} from 'lucide-react';
import { getApiUrl } from '../config';
import { useSession } from '../contexts/SessionContext';

const ConsoleDashboard = () => {
  const { user } = useSession();
  const navigate = useNavigate();
  const [stats, setStats] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [systemStatus, setSystemStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const quickActions = [
    { name: 'Drop-in SDK Integration', path: '/console/documentation', icon: Code, color: 'bg-purple-500', description: 'Just import and wrap your agent - everything else is automatic!' },
    { name: 'Manage Agents', path: '/console/admin/agents', icon: UserCheck, color: 'bg-blue-500' },
    { name: 'Agent Analytics', path: '/console/analytics/agents', icon: BarChart3, color: 'bg-green-500' },
    { name: 'Feedback Dashboard', path: '/console/analytics/feedback', icon: MessageSquare, color: 'bg-purple-500' },
    { name: 'Webhook Management', path: '/console/admin/webhooks', icon: Webhook, color: 'bg-orange-500' },
    { name: 'Customer Analytics', path: '/console/analytics/customers', icon: Users, color: 'bg-pink-500' },
    { name: 'API Keys', path: '/console/admin/api-keys', icon: Shield, color: 'bg-indigo-500' }
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'presentation': return <Presentation className="h-4 w-4 text-blue-500" />;
      case 'faq': return <HelpCircle className="h-4 w-4 text-green-500" />;
      case 'agent': return <UserCheck className="h-4 w-4 text-purple-500" />;
      case 'customer': return <Users className="h-4 w-4 text-orange-500" />;
      case 'webhook': return <Webhook className="h-4 w-4 text-indigo-500" />;
      case 'feedback': return <BarChart3 className="h-4 w-4 text-blue-500" />;
      default: return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch stats
      const statsResponse = await axios.get(getApiUrl('/console/dashboard/stats'), {
        withCredentials: true
      });
      setStats(statsResponse.data.stats || []);
      
      // Fetch recent activity
      const activityResponse = await axios.get(getApiUrl('/console/dashboard/activity'), {
        withCredentials: true
      });
      setRecentActivity(activityResponse.data.activities || []);
      
      // Fetch system status
      const statusResponse = await axios.get(getApiUrl('/console/dashboard/system-status'), {
        withCredentials: true
      });
      setSystemStatus(statusResponse.data.status_items || []);
      
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard data');
      
      // Fallback to empty arrays if API fails
      setStats([]);
      setRecentActivity([]);
      setSystemStatus([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleQuickActionClick = (path, e) => {
    e.preventDefault();
    navigate(path);
  };

  const handleViewAllActivity = (e) => {
    e.preventDefault();
    navigate('/console/analytics/feedback');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            <span className="ml-3 text-gray-600">Loading dashboard...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-center py-8">
            <AlertCircle className="h-8 w-8 text-red-500" />
            <div className="ml-3">
              <p className="text-red-600 font-medium">Error loading dashboard</p>
              <p className="text-sm text-gray-500">{error}</p>
              <button 
                onClick={fetchDashboardData}
                className="mt-2 text-sm text-blue-600 hover:text-blue-700"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.username || 'User'}!
        </h1>
        <p className="text-gray-600 mt-1">Here's what's happening with your Enable.AI account today.</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => {
          // Special styling for health-related stats
          const isHealthStat = stat.name.includes('Healthy') || stat.name.includes('Unhealthy');
          const isUnhealthy = stat.name.includes('Unhealthy');
          const isHealthy = stat.name.includes('Healthy');
          
          return (
            <div key={stat.name} className={`bg-white rounded-lg shadow-sm border p-6 ${
              isHealthStat ? (isUnhealthy ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50') : 'border-gray-200'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${
                    isHealthStat ? (isUnhealthy ? 'text-red-600' : 'text-green-600') : 'text-gray-600'
                  }`}>
                    {stat.name}
                  </p>
                  <p className={`text-2xl font-bold ${
                    isHealthStat ? (isUnhealthy ? 'text-red-900' : 'text-green-900') : 'text-gray-900'
                  }`}>
                    {stat.value}
                  </p>
                  {isHealthStat && (
                    <p className={`text-xs mt-1 ${
                      isUnhealthy ? 'text-red-500' : 'text-green-500'
                    }`}>
                      {isHealthy ? 'Good performance' : 'Needs attention'}
                    </p>
                  )}
                </div>
                <div className={`flex items-center text-sm ${
                  stat.changeType === 'positive' ? 'text-green-600' : 
                  stat.changeType === 'negative' ? 'text-red-600' : 'text-gray-600'
                }`}>
                  <TrendingUp className="h-4 w-4 mr-1" />
                  {stat.change}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-4">
            {quickActions.map((action) => (
              <button
                key={action.name}
                onClick={(e) => handleQuickActionClick(action.path, e)}
                className="flex items-center p-4 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-colors text-left w-full"
              >
                <div className={`p-2 rounded-lg ${action.color} mr-3`}>
                  <action.icon className="h-5 w-5 text-white" />
                </div>
                <span className="text-sm font-medium text-gray-900">{action.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
          <div className="space-y-4">
            {recentActivity.length > 0 ? (
              recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {activity.action}
                    </p>
                    <p className="text-sm text-gray-500 flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      {activity.time_ago}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500">No recent activity</p>
              </div>
            )}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button 
              onClick={handleViewAllActivity}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              View all activity →
            </button>
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">System Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {systemStatus.map((status) => (
            <div key={status.name} className="flex items-center space-x-3">
              {status.healthy ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-500" />
              )}
              <div>
                <p className="text-sm font-medium text-gray-900">{status.name}</p>
                <p className="text-sm text-gray-500">{status.status}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ConsoleDashboard; 